/**
 * Paddle Integration Utility
 * Handles international credit purchases via Paddle Billing
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// Paddle Environment Configuration
const PADDLE_ENVIRONMENT = process.env.REACT_APP_PADDLE_ENVIRONMENT || 'sandbox';
const PADDLE_CLIENT_TOKEN = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;

/**
 * Paddle Price IDs for fixed credit packages
 * Pricing is managed in Paddle Dashboard with country-specific overrides
 */
export const PADDLE_PRICE_IDS = {
  10: 'pri_01kbb2admdshhbme67pkhnfrrv',    // $2.00
  25: 'pri_01kbc3qmq7bxkeymsszskn3drg',    // $5.00
  50: 'pri_01kbc3t3e0297j42xenrvh9gty',    // $8.00
  100: 'pri_01kbc41fbs8ymbzwde3j46eaeok k',   // $16.00
  250: 'pri_01kbc47xqfb7xO1n1jexyjgsyp',   // $35.00
  500: 'pri_01kbc4a9pyd9zamg1mfkfxm4na'    // $50.00
};

/**
 * Initialize Paddle.js
 * Must be called before using Paddle checkout
 */
export function initializePaddle() {
  console.log('🔧 Attempting to initialize Paddle...');
  console.log('Window object exists:', typeof window !== 'undefined');
  console.log('Paddle.js loaded:', !!window.Paddle);
  console.log('Client token exists:', !!PADDLE_CLIENT_TOKEN);
  console.log('Client token value:', PADDLE_CLIENT_TOKEN ? PADDLE_CLIENT_TOKEN.substring(0, 10) + '...' : 'MISSING');
  console.log('Environment:', PADDLE_ENVIRONMENT);

  if (typeof window === 'undefined' || !window.Paddle) {
    console.error('❌ Paddle.js not loaded. Make sure to include Paddle script in index.html');
    return false;
  }

  if (!PADDLE_CLIENT_TOKEN) {
    console.error('❌ Paddle client token not configured. Set REACT_APP_PADDLE_CLIENT_TOKEN in .env');
    return false;
  }

  try {
    console.log('🚀 Calling Paddle.Initialize with config:', {
      token: PADDLE_CLIENT_TOKEN.substring(0, 10) + '...'
    });

    // Set environment BEFORE Initialize - this is required for sandbox!
    // Paddle.js defaults to production, so we must explicitly set sandbox mode
    if (PADDLE_ENVIRONMENT === 'sandbox' || PADDLE_CLIENT_TOKEN.startsWith('test_')) {
      window.Paddle.Environment.set('sandbox');
      console.log('🏖️ Paddle environment set to sandbox');
    }

    window.Paddle.Initialize({
      token: PADDLE_CLIENT_TOKEN,
      eventCallback: function(data) {
        console.log('📨 Paddle event:', data);
      }
    });

    const environment = PADDLE_CLIENT_TOKEN.startsWith('test_') ? 'sandbox' : 'production';
    console.log(`✅ Paddle initialized successfully in ${environment} mode`);
    return true;
  } catch (error) {
    console.error('❌ Error initializing Paddle:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack
    });
    return false;
  }
}

/**
 * Get Paddle Price ID for a credit amount
 * @param {number} credits - Credit amount (10, 25, 50, 100, 250, or 500)
 * @returns {string} Paddle Price ID
 */
export function getPaddlePriceId(credits) {
  const priceId = PADDLE_PRICE_IDS[credits];

  if (!priceId) {
    throw new Error(`No price ID configured for ${credits} credits. Valid amounts: ${Object.keys(PADDLE_PRICE_IDS).join(', ')}`);
  }

  console.log(`📍 Price ID for ${credits} credits: ${priceId}`);
  return priceId;
}

/**
 * Open Paddle Checkout
 * Supports both fixed packages (priceId) and variable quantity (quantity + totalPrice)
 *
 * @param {Object} options - Checkout options
 * @param {string} [options.priceId] - Paddle Price ID (for fixed packages)
 * @param {number} [options.quantity] - Quantity of credits (for variable pricing)
 * @param {number} [options.totalPrice] - Total price in base currency units (for variable pricing)
 * @param {string} [options.currency] - Currency code (required for variable pricing)
 * @param {string} options.userId - User ID for tracking
 * @param {string} options.email - Customer email
 * @param {number} options.credits - Number of credits being purchased
 * @param {string} options.countryCode - Country code for analytics
 * @param {Function} options.successCallback - Called on successful payment
 * @param {Function} options.closeCallback - Called when checkout closes
 * @returns {Promise<void>}
 */
export async function openPaddleCheckout(options) {
  const {
    priceId,
    quantity,
    totalPrice,
    currency,
    userId,
    email,
    credits,
    countryCode,
    successCallback,
    closeCallback
  } = options;

  if (!window.Paddle) {
    throw new Error('Paddle not initialized. Call initializePaddle() first.');
  }

  // Determine if using fixed package or variable pricing
  const useCustomPrice = !priceId && quantity && totalPrice && currency;

  if (!priceId && !useCustomPrice) {
    throw new Error('Must provide either priceId or (quantity + totalPrice + currency)');
  }

  try {
    let checkoutConfig;

    if (useCustomPrice) {
      // Variable pricing with custom price API
      // Convert price to minor currency units (cents)
      const amountInCents = Math.round(totalPrice * 100);

      console.log('Opening Paddle checkout with custom price...', {
        quantity,
        totalPrice,
        amountInCents,
        currency,
        credits,
        countryCode
      });

      checkoutConfig = {
        items: [{
          quantity: 1, // Always 1 because price already includes quantity calculation
          price: {
            description: `${credits} Credits - PDF Statement Converter`,
            unitPrice: {
              amount: amountInCents.toString(), // Must be string
              currencyCode: currency.toUpperCase()
            }
          }
        }],
        customer: {
          email
        },
        customData: {
          userId,
          credits,
          quantity: credits, // Store quantity for webhook validation
          countryCode,
          pricingType: 'variable'
        },
        settings: {
          displayMode: 'overlay',
          theme: 'light',
          locale: 'en'
        },
        successCallback: (data) => {
          console.log('✅ Paddle payment successful:', data);
          if (successCallback) {
            successCallback(data);
          }
        },
        closeCallback: () => {
          console.log('Paddle checkout closed');
          if (closeCallback) {
            closeCallback();
          }
        }
      };
    } else {
      // Fixed package with priceId (legacy support)
      console.log('Opening Paddle checkout with priceId...', {
        priceId,
        credits,
        countryCode
      });

      checkoutConfig = {
        items: [{
          priceId,
          quantity: 1
        }],
        customer: {
          email
        },
        customData: {
          userId,
          credits,
          countryCode,
          pricingType: 'fixed'
        },
        settings: {
          displayMode: 'overlay',
          theme: 'light',
          locale: 'en'
        },
        successCallback: (data) => {
          console.log('✅ Paddle payment successful:', data);
          if (successCallback) {
            successCallback(data);
          }
        },
        closeCallback: () => {
          console.log('Paddle checkout closed');
          if (closeCallback) {
            closeCallback();
          }
        }
      };
    }

    window.Paddle.Checkout.open(checkoutConfig);
  } catch (error) {
    console.error('Error opening Paddle checkout:', error);
    throw error;
  }
}

/**
 * Wait for Paddle webhook to process and credits to be added
 * Polls Firestore every 2 seconds for up to 5 minutes
 *
 * @param {string} transactionId - Paddle transaction ID
 * @param {string} userId - User ID to check credits for
 * @param {number} expectedCredits - Number of credits that should be added
 * @returns {Promise<boolean>} Resolves when credits are added, rejects on timeout
 */
export const waitForPaddleWebhookProcessing = async (transactionId, userId, expectedCredits) => {
  const maxWaitTime = 300000; // 5 minutes
  const pollInterval = 2000; // 2 seconds
  const startTime = Date.now();

  console.log(`⏳ Waiting for webhook to process transaction ${transactionId}...`);
  console.log(`📊 Checking for ${expectedCredits} credits to be added to user ${userId}`);

  // Get initial credit balance
  const userRef = doc(db, 'users', userId);
  const initialDoc = await getDoc(userRef);
  const initialCredits = initialDoc.data()?.credits || 0;
  const expectedFinalCredits = initialCredits + expectedCredits;

  console.log(`💰 Initial credits: ${initialCredits}, Expected after: ${expectedFinalCredits}`);

  return new Promise((resolve, reject) => {
    const checkCredits = async () => {
      try {
        // Check if max wait time exceeded
        const elapsedTime = Date.now() - startTime;
        if (elapsedTime >= maxWaitTime) {
          console.error('❌ Webhook processing timeout after 5 minutes');
          reject(new Error(
            'Payment processing is taking longer than expected. ' +
            'Your credits should arrive within 10 minutes. ' +
            'If not, please contact support with transaction ID: ' + transactionId
          ));
          return;
        }

        // Check current credit balance
        const currentDoc = await getDoc(userRef);
        const currentCredits = currentDoc.data()?.credits || 0;

        console.log(`🔍 Polling... Current credits: ${currentCredits} (Expected: ${expectedFinalCredits})`);

        if (currentCredits >= expectedFinalCredits) {
          // Credits have been added!
          console.log('✅ Credits detected! Webhook processing complete.');
          resolve(true);
        } else {
          // Continue polling
          setTimeout(checkCredits, pollInterval);
        }
      } catch (error) {
        console.error('Error checking credits:', error);
        // Don't reject immediately, continue polling
        setTimeout(checkCredits, pollInterval);
      }
    };

    // Start checking after 1 second delay
    setTimeout(checkCredits, 1000);
  });
};

/**
 * Get available credit packages for Paddle
 * @returns {number[]} Array of available credit amounts
 */
export function getAvailablePackages() {
  return Object.keys(PADDLE_PRICE_IDS).map(Number).sort((a, b) => a - b);
}

/**
 * Get supported credit packages for international purchases
 * @returns {Array<number>} Array of supported credit amounts
 */
export function getSupportedPackages() {
  return [10, 25, 50, 100, 250, 500];
}

/**
 * Check if a credit amount is a valid Paddle package
 * @param {number} credits - Credit amount to check
 * @returns {boolean} True if valid package
 */
export function isValidPackage(credits) {
  return getSupportedPackages().includes(credits);
}

/**
 * Get recommended package based on user needs
 * @param {number} desiredCredits - Number of credits user wants
 * @returns {number} Closest valid package size
 */
export function getRecommendedPackage(desiredCredits) {
  const packages = getSupportedPackages();

  // Find the smallest package that meets the need
  for (const pkg of packages) {
    if (pkg >= desiredCredits) {
      return pkg;
    }
  }

  // If desired amount exceeds all packages, return largest
  return packages[packages.length - 1];
}

/**
 * Check if Paddle is properly configured
 * @returns {Object} Configuration status
 */
export function checkPaddleConfiguration() {
  const issues = [];

  if (!PADDLE_CLIENT_TOKEN) {
    issues.push('REACT_APP_PADDLE_CLIENT_TOKEN not set in environment variables');
  }

  if (!window.Paddle) {
    issues.push('Paddle.js script not loaded in index.html');
  }

  // Check if any price IDs are still placeholders
  const hasPlaceholders = Object.values(PADDLE_PRICE_IDS).some(country =>
    Object.values(country).some(priceId => priceId.includes('REPLACE_ME'))
  );

  if (hasPlaceholders) {
    issues.push('Some Price IDs are still placeholder values - update with real IDs from Paddle Dashboard');
  }

  return {
    configured: issues.length === 0,
    issues,
    environment: PADDLE_ENVIRONMENT
  };
}

// Export configuration status on load
console.log('🏓 Paddle configuration loaded:', checkPaddleConfiguration());
